// Google Maps loader service - runtime-loads the API key from /api/config.
let loadPromise: Promise<void> | null = null;
const SCRIPT_ID = 'google-maps-js-api';
const LIBRARIES = 'drawing,places,marker,visualization,maps3d';
const SCRIPT_TIMEOUT_MS = 30_000;
let loadAttempt = 0;

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function buildTimeMapsKey(): string {
  return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
}

async function fetchRuntimeMapsKey(): Promise<string> {
  const res = await fetch('/api/config', { cache: 'no-store' });
  const contentType = res.headers.get('content-type') || '';
  if (!res.ok) {
    throw new Error('Google Maps config failed to load');
  }
  // A Pages deploy without Functions returns 200 HTML for /api/config.
  // That is not "key missing" — retry instead of treating it as unconfigured.
  if (!contentType.includes('application/json')) {
    throw new Error('Google Maps config is temporarily unavailable');
  }
  const config = await res.json().catch(() => ({}));
  const key = typeof config?.googleMapsApiKey === 'string' ? config.googleMapsApiKey.trim() : '';
  if (!key) {
    throw new Error('Google Maps API key is not configured');
  }
  return key;
}

async function loadMapsApiKey(): Promise<string> {
  const buildTimeKey = buildTimeMapsKey();
  if (buildTimeKey) return buildTimeKey;

  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await fetchRuntimeMapsKey();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('Google Maps config failed to load');
      if (lastError.message.includes('not configured')) break;
      if (attempt < 2) await sleep(350 * (attempt + 1));
    }
  }
  throw lastError || new Error('Google Maps config failed to load');
}

export function loadGoogleMaps(): Promise<void> {
  // The namespace may exist before Google's async readiness callback runs.
  // All callers must wait for the same in-flight initialization.
  if (loadPromise) {
    return loadPromise;
  }
  if (isGoogleMapsLoaded()) return Promise.resolve();

  loadPromise = loadMapsApiKey().then((apiKey) => new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing || document.createElement('script');
    // Separate callbacks prevent a late response from completing a newer retry.
    const callbackName = `__zwnGoogleMapsReady${++loadAttempt}`;
    const callbacks = window as unknown as Record<string, (() => void) | undefined>;
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      script.removeEventListener('load', onExistingLoad);
      script.removeEventListener('error', onError);
      if (error) {
        // A failed element will never emit another load event. Remove it so
        // the next call makes a real request instead of waiting on a dead tag.
        script.remove();
        callbacks[callbackName] = () => {}; // tolerate a late provider response
        reject(error);
      } else {
        delete callbacks[callbackName];
        resolve();
      }
    };
    const onReady = () => finish(isGoogleMapsLoaded()
      ? undefined : new Error('Google Maps API loaded without importLibrary'));
    const onError = () => finish(new Error('Google Maps API failed to load'));
    const onExistingLoad = () => { if (isGoogleMapsLoaded()) onReady(); };
    const timer = window.setTimeout(() => finish(new Error('Google Maps API loading timed out')), SCRIPT_TIMEOUT_MS);
    script.addEventListener('error', onError, { once: true });
    if (existing) {
      // Compatibility with a script installed before this loader owned it.
      // The watchdog also releases old tags whose load/error already fired.
      script.addEventListener('load', onExistingLoad, { once: true });
      if (isGoogleMapsLoaded()) onReady();
    } else {
      callbacks[callbackName] = onReady;
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=beta&libraries=${encodeURIComponent(LIBRARIES)}&loading=async&callback=${callbackName}`;
      document.head.appendChild(script);
    }
  })).catch((err) => {
    // Do not cache a rejection — pin map / later screens must be able to retry.
    loadPromise = null;
    throw err;
  });

  return loadPromise;
}

export function isGoogleMapsLoaded(): boolean {
  return typeof window.google?.maps?.importLibrary === 'function';
}
